import { CalendarStatus } from '@prisma/client'
import { String } from 'effect'
import NextHead from 'next/head'
import { useRouter } from 'next/router'
import Script from 'next/script'
import { useTranslation } from 'next-i18next'
import { useContext } from 'react'
import { LocaleContext } from 'components/common/LocaleContext'
import { getDateForTimezone } from 'components/common/date.utils'
import { useHrefLang } from 'containers/seoPages/useHrefLang'
import { organizer } from 'dataParsing/stucturedData'

interface EventHeadProps {
  calendarStatus: CalendarStatus
  city: string
  startDate: Date | null
  endDate: Date | null
  timeZone: string | null
  currency: string
  name: string
  image?: string | null
}

export default function Head({
  calendarStatus,
  city,
  startDate,
  endDate,
  timeZone,
  currency,
  name,
  image
}: EventHeadProps) {
  const { t } = useTranslation(['common', 'event'])
  const hrefLangs = useHrefLang()
  const { dateStringLocale } = useContext(LocaleContext)
  const { query, locale, defaultLocale } = useRouter()
  const { slugs } = query
  const [slug] = slugs!
  const appUri = process.env.APP_URI
  const currentUrl = `${appUri}${
    locale === defaultLocale ? '' : `/${locale}`
  }/event/${slug}`

  const year = startDate
    ? getDateForTimezone(startDate, timeZone, dateStringLocale, {
        year: 'numeric'
      })
    : null

  const formattedStartDate = startDate
    ? String.capitalize(
        getDateForTimezone(startDate, timeZone, dateStringLocale, {
          weekday: 'long',
          month: 'long',
          day: 'numeric'
        })
      )
    : null
  const formattedEndDate = endDate
    ? String.capitalize(
        getDateForTimezone(endDate, timeZone, dateStringLocale, {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          year: 'numeric'
        })
      )
    : null

  const eventTitle = name.toLowerCase().includes(city.toLowerCase())
    ? `${name} ${year}`
    : t('eventPage.eventMicroTitle', { name, year, city })

  const idJsonObject = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: name,
    startDate: formattedStartDate,
    endDate: formattedEndDate,
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    location: {
      '@type': 'Place',
      name: name,
      address: {
        '@type': 'PostalAddress',
        addressLocality: city,
        addressCountry: 'France'
      }
    },
    description: name,
    offers: {
      '@type': 'Offer',
      url: currentUrl,
      availability: 'LimitedAvailability',
      price: '',
      priceCurrency: currency
    },
    // priceValidUntil: '2021-03-23T17:59:33.345Z',
    validFrom: 'Sep 17, 2021 10:00 pm',
    organizer: organizer
  }

  return (
    <NextHead>
      <title>{eventTitle}</title>
      <meta
        name="description"
        content={`[${t(calendarStatus)}] ${t('event:headDescription')}`}
      />

      <link rel="icon" href="/favicon.ico" />
      <link rel="canonical" href={currentUrl} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta
        property="og:title"
        content={`${name} - ${city} | ${formattedStartDate}`}
      />
      <meta
        name="twitter:title"
        content={`${name} - ${city} | ${formattedStartDate}`}
      />
      <meta property="og:site_name" content="Miles Republic" />
      <meta name="twitter:site_name" content="Miles Republic" />
      <meta
        property="og:description"
        content={`[${t(calendarStatus)}] ${t('event:headDescription')}`}
      />
      <meta
        name="twitter:description"
        content={`[${t(calendarStatus)}] ${t('event:headDescription')}`}
      />
      <meta
        name="twitter:image"
        content={image ? image : `${appUri}/assets/banner.webp`}
      />
      <meta
        property="og:image"
        content={image ? image : `${appUri}/assets/banner.webp`}
      />
      <meta property="fb:app_id" content="429733861348121" />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:type" content="website" />
      <Script
        type="application/ld+json"
        id="event-head"
        strategy="beforeInteractive"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(idJsonObject) }}
      />
      {...hrefLangs}
    </NextHead>
  )
}
